import React, { useState, useEffect } from 'react';
import {
  Boxes,
  Plus,
  Search,
  Building2,
  FolderKanban,
  ArrowRight,
  Send,
  History,
} from 'lucide-react';
import { LegacyInventoryItem, StockTransactionItem } from '../types/index.ts';

interface InventoryPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const InventoryPage: React.FC<InventoryPageProps> = ({ onNavigate }) => {
  const [items, setItems] = useState<LegacyInventoryItem[]>([]);
  const [transactions, setTransactions] = useState<StockTransactionItem[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showIssueModal, setShowIssueModal] = useState(false);

  const [issueForm, setIssueForm] = useState({
    itemId: '',
    projectId: '',
    quantity: '50',
    remarks: 'Issued for branch renovation floor work',
  });

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const [iRes, tRes, pRes] = await Promise.all([
        fetch('/api/items'),
        fetch('/api/stock-transactions'),
        fetch('/api/projects'),
      ]);
      if (iRes.ok) {
        const iData = await iRes.json();
        if (Array.isArray(iData)) {
          setItems(iData);
          if (iData.length > 0) setIssueForm((prev) => ({ ...prev, itemId: String(iData[0].id) }));
        }
      }
      if (tRes.ok) {
        const tData = await tRes.json();
        if (Array.isArray(tData)) setTransactions(tData);
      }
      if (pRes.ok) {
        const pData = await pRes.json();
        if (Array.isArray(pData)) {
          setProjects(pData);
          if (pData.length > 0) setIssueForm((prev) => ({ ...prev, projectId: String(pData[0].id) }));
        }
      }
    } catch (err) {
      console.warn('Inventory fetch notice:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
  }, []);

  const handleIssue = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/stock-transactions/issue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(issueForm),
      });
      if (res.ok) {
        setShowIssueModal(false);
        fetchInventory();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const formatPKR = (val: string | number) => {
    return 'Rs. ' + Math.round(Number(val || 0)).toLocaleString('en-PK');
  };

  const selectedItemObj = items.find((i) => i.id === Number(issueForm.itemId));
  const estimatedIssueCost = selectedItemObj
    ? Number(selectedItemObj.standardCost) * Number(issueForm.quantity || 0)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Materials &amp; Inventory Ledger
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-200">
              Project Issues
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Central store balance, material issue to branch projects, and live cost accumulation.
          </p>
        </div>

        <button
          onClick={() => setShowIssueModal(true)}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors self-start sm:self-auto"
        >
          <Send className="w-4 h-4 text-amber-400" />
          <span>Issue Material to Project</span>
        </button>
      </div>

      {/* Item Master Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {(Array.isArray(items) ? items : []).map((item) => (
          <div key={item.id} className="bg-white rounded-xl border border-slate-200 shadow-2xs p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-slate-100 text-slate-700 rounded">
                {item.category}
              </span>
              <span className="text-xs font-mono font-bold text-blue-900">{item.code}</span>
            </div>

            <div className="font-bold text-sm text-slate-900">{item.name}</div>

            <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
              <div>
                <span className="text-slate-400 block text-[10px]">Current Stock</span>
                <span className="font-bold text-slate-800">
                  {Number(item.currentStock).toLocaleString()} {item.unit}
                </span>
              </div>
              <div className="text-right">
                <span className="text-slate-400 block text-[10px]">Std. Cost</span>
                <span className="font-bold text-slate-800">{formatPKR(item.standardCost)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Stock Transaction Ledger */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
        <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
            Stock Issue &amp; Movement Ledger
          </span>
          <span className="text-xs text-slate-500">{transactions.length} records</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Ref #</th>
                <th className="py-3 px-4">Movement Type</th>
                <th className="py-3 px-4">Material Item</th>
                <th className="py-3 px-4">Issued To Project</th>
                <th className="py-3 px-4 text-right">Quantity</th>
                <th className="py-3 px-4 text-right">Unit Rate</th>
                <th className="py-3 px-4 text-right">Total Cost (PKR)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(Array.isArray(transactions) ? transactions : []).map((t) => (
                <tr key={t.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-3 px-4 font-mono font-bold text-blue-900">{t.referenceNo}</td>
                  <td className="py-3 px-4">
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-900 border border-amber-200">
                      {t.transactionType.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-semibold text-slate-800">{t.itemName}</td>
                  <td className="py-3 px-4 text-slate-700">{t.projectNumber}</td>
                  <td className="py-3 px-4 text-right font-medium">
                    {Number(t.quantity).toLocaleString()} {t.unit}
                  </td>
                  <td className="py-3 px-4 text-right text-slate-600">{formatPKR(t.unitCost)}</td>
                  <td className="py-3 px-4 text-right font-bold text-slate-900">{formatPKR(t.totalCost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Issue Material Modal */}
      {showIssueModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Issue Material to Project</h3>
              <button
                onClick={() => setShowIssueModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleIssue} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Select Material Item *</label>
                <select
                  required
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={issueForm.itemId}
                  onChange={(e) => setIssueForm({ ...issueForm, itemId: e.target.value })}
                >
                  {(Array.isArray(items) ? items : []).map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name} (Stock: {i.currentStock} {i.unit} @ {formatPKR(i.standardCost)})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Target Project *</label>
                <select
                  required
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={issueForm.projectId}
                  onChange={(e) => setIssueForm({ ...issueForm, projectId: e.target.value })}
                >
                  {(Array.isArray(projects) ? projects : []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.projectNumber} - {p.projectName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Quantity to Issue *</label>
                <input
                  type="number"
                  required
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={issueForm.quantity}
                  onChange={(e) => setIssueForm({ ...issueForm, quantity: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Remarks / Location on Site</label>
                <input
                  type="text"
                  placeholder="e.g. Issued for floor leveling & screed"
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={issueForm.remarks}
                  onChange={(e) => setIssueForm({ ...issueForm, remarks: e.target.value })}
                />
              </div>

              {/* Automatic Calculation Preview */}
              <div className="bg-blue-50/70 p-3 rounded-lg border border-blue-200 flex items-center justify-between text-xs">
                <span className="text-slate-700 font-medium">Automatic Material Cost Impact:</span>
                <span className="text-sm font-bold text-blue-900">{formatPKR(estimatedIssueCost)}</span>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowIssueModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg font-semibold"
                >
                  Issue Material
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
